<template>
  <ValidationObserver ref="registerWorkerForm" slim tag="div">
    <form role="form" @submit.prevent="register_worker_submit" class="row" autocomplete="off">
      <!-- Personal Information -->
      <div class="col-12">
        <b-card header="Personal Information" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <!-- Title -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="title">Title</label>
              <multiselect v-model="person.title" id="title" name="title" placeholder="" :options="ms_opts.titles" label="value" :show-labels="false"></multiselect>
            </div>
            <!-- First Name -->
            <ValidationProvider rules="required" v-slot="{errors, classes}" name="First Name" tag="div" class="col-xl-4 form-group">
              <label class="form-label" for="first_name">First Name(s)</label>
              <input type="search" v-model="person.first_name" id="first_name" name="first_name" class="form-input requiredField" :class="classes" maxlength="255"
                     @input="forceCapitalizeApostrophe($event, person, 'first_name')">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Surname -->
            <ValidationProvider rules="required" v-slot="{errors, classes}" name="Surname" tag="div" class="col-xl-4 form-group">
              <label class="form-label" for="surname">Surname</label>
              <input type="search" v-model="person.surname" id="surname" name="surname" class="form-input requiredField" :class="classes" maxlength="255"
                     @input="forceCapitalizeApostrophe($event, person, 'surname')">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Date of Birth -->
            <ValidationProvider rules="required" v-slot="{errors, classes}" name="Date of Birth" tag="div" class="col-xl-3 form-group">
              <label class="form-label" for="dob">Date of Birth</label>
              <datepicker v-model="person.dob" id="dob" name="dob" :input-class="['date-input', 'requiredField', classes]" :format="dateFormat" :clear-button="true"
                          clear-button-icon="far fa-calendar-times" @input="select_dob" :use-utc="true"></datepicker>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- NI Number -->
            <ValidationProvider :rules="{ required: true, regex: /^[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9][A-Z]$/ }" v-slot="{errors, classes}" name="NI Number" tag="div"
                                class="col-xl-3 form-group">
              <label class="form-label" for="ni_number">NI Number</label>
              <input type="search" v-model="profile.ni_number" id="ni_number" name="ni_number" class="form-input requiredField" :class="classes"
                     @input="forceUppercase($event, profile, 'ni_number')" @blur="check_if_ni_exists(profile.ni_number)">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Nationality -->
            <div class="col-xl-3 form-group">
              <label class="form-label" for="nationality">Nationality</label>
              <multiselect v-model="person.nationality" id="nationality" name="nationality" placeholder="" :options="ms_opts.nationalities" label="nationality"
                           :show-labels="false"></multiselect>
            </div>
            <!-- UK Citizen -->
            <div class="col-xl-3 form-group">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="is_uk_citizen">UK Citizen?</label>
                <div class="col-md-4 form-group">
                  <bootstrap-toggle v-model="person.is_uk_citizen" id="is_uk_citizen" name="is_uk_citizen" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <!-- Advisories for record Nationality -->
            <div class="col-12" v-if="check_uk_ireland">
              <div class="worker_record_type worker row" style="margin-left: 0; margin-right: 0;">
                <div class="col-lg-3"><h5>UK / Ireland<br>Right To Work</h5></div>
                <div class="col-lg-9"><p>To ensure that the worker has the right to work in the UK we require a copy of:<br><strong>Passport</strong> and
                  <strong>Proof of Address</strong></p>
                </div>
              </div>
            </div>
            <div class="col-12" v-else-if="check_eu">
              <div class="worker_record_type company row" style="margin-left: 0; margin-right: 0;">
                <div class="col-lg-3"><h5>EU<br>Right To Work</h5></div>
                <div class="col-lg-9"><p>To ensure that the worker has the right to work in the UK we require a copy of:<br><strong>Passport</strong>,
                   and <strong>Proof of settlement status</strong> (code or image of letter)</p>
                </div>
              </div>
            </div>
            <div class="col-12" v-else-if="check_international">
              <div class="worker_record_type international row" style="margin-left: 0; margin-right: 0;">
                <div class="col-lg-3"><h5>International<br>Right To Work</h5></div>
                <div class="col-lg-9"><p>To ensure that the worker has the right to work in the UK we require a copy of:<br><strong>Passport</strong>,
                  and <strong>Proof of settlement status</strong> (code or image of letter)</p>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <!-- Primary / Website Login Email -->
      <div class="col-xl-4">
        <b-card header="Primary / Website Login Email" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <!-- Email Address -->
            <ValidationProvider rules="email|required" v-slot="{errors, classes}" name="Login Email Address" tag="div" class="col-12 form-group">
              <label class="form-label" for="email">Login Email Address</label>
              <div class="input-group">
                <input type="search" v-model="emails.website_login.email_address" id="email" class="form-input requiredField" :class="classes"
                       @blur="check_if_email_exists(emails.website_login.email_address)">
                <div class="input-group-append"><span class="input-group-text"><i class="fas fa-at"></i></span></div>
              </div>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <div class="col-12">
              <p class="mb-2">This <strong>email must be unique to the worker</strong>, it is the email address used to link the workers website profile to their ORCA record.</p>
            </div>
            <div class="col-12 form-group" v-if="emails.website_login.email_address === ''">
              <button type="button" class="btn orcaBtn w-100 sm_btn grey_bg" @click.prevent="no_email_address">Don't Know or Have Email</button>
            </div>
            
              <!-- create_website_account -->
              <div v-if="this.crmType === 'payroll'" class="col-12 form-group">
                <div v-if="this.emails.website_login.email_address && !this.emails.website_login.email_address.includes('@orca-no-email.com')" class="row">
                  <label class="col-xl-8 cardLabel" for="create_website_account">Create account via geniusmoney.co.uk. ?</label>
                  <div  class="col-md-4 form-group">
                    <bootstrap-toggle v-model="create_website_account" id="create_website_account" name="create_website_account" :options="{ on: 'Yes', off: 'No' }"/>
                  </div>
                </div>
              </div>
              
          </div>
        </b-card>
      </div>
      <!-- Current Employment Status -->
      <div class="col-xl-8">
        <b-card header="Current Employment Status" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <!-- Employment Status -->
            <ValidationProvider rules="required" v-slot="{errors, classes}" name="Employment Status" tag="div" class="col-lg-6 form-group">
              <label class="form-label" for="employment_type">Current Status</label>
              <multiselect v-model="company_info.employment_type" id="employment_type" class="requiredField" :class="classes" name="employment_type" placeholder=""
                           :options="ms_opts.employment_types" label="value" :show-labels="false"></multiselect>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Job Title -->
            <div class="col-lg-6 form-group job_title_multiselect">
              <label class="form-label" for="job_title">Current Job Title</label>
              <multiselect v-model="profile.job_title" placeholder="" id="job_title" label="job_title_label" track-by="id" :options="ms_opts.job_titles"
                           :option-height="200" :custom-label="job_title_label" :show-labels="false">
                <template slot="singleLabel" slot-scope="props">
                  <div class="option__desc_single">
                    <span class="option__title">{{ props.option.job_title }}</span>
                  </div>
                </template>
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <span class="option__title">{{ props.option.job_title }}</span>
                    <span class="option__small">{{ props.option.sector_name }}</span>
                  </div>
                </template>
              </multiselect>
            </div>
            <!-- Advisory for record Company/Worker -->
            <div class="col-12">
              <div class="row" v-if="company_info.employment_type !== null">
                <div class="col-12" v-if="company_info.employment_type.id === 32">
                  <div class="worker_record_type company row ml-0 mr-0">
                    <div class="col-lg-3"><h5>Company Record</h5></div>
                    <div class="col-lg-9"><p>On registration this will create 2 finance references. One for the person and one for the Limited Company.<br>
                      <strong>Please ensure that you correctly enter the company information as this is the information that will be used to pay the Limited Company.</strong></p>
                    </div>
                  </div>
                </div>
                <div class="col-12" v-else>
                  <div class="worker_record_type worker row ml-0 mr-0">
                    <div class="col-lg-3"><h5>Worker Record</h5></div>
                    <div class="col-lg-9"><p>This will create a record with a single finance reference.<br>
                      <strong>Please ensure that you correctly enter the personal information as this is the information that will be used to pay the worker.</strong></p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <!-- Personal Contact Information -->
      <div class="col-xl-8">
        <b-card header="Personal Contact Information" header-tag="h4" body-class="mb-3 pb-2">
          <!-- Email / Mobile / telephone -->
          <div class="row">
            <!-- Mobile Number -->
            <ValidationProvider rules="numeric|max:15" v-slot="{errors, classes}" name="Mobile" tag="div" class="col-xl-4 form-group">
              <label class="form-label" for="mobile">Personal Mobile Number</label>
              <div class="input-group">
                <input type="search" v-model="phones.mobile.phone_number" id="mobile" name="mobile" class="form-input" :class="classes" maxlength="15">
                <div class="input-group-append"><span class="input-group-text"><i class="fas fa-mobile-alt"></i></span></div>
              </div>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Telephone Number -->
            <ValidationProvider rules="numeric|max:15" v-slot="{errors, classes}" name="Landline Telephone" tag="div" class="col-xl-4 form-group">
              <label class="form-label" for="telephone">Personal Landline Phone Number</label>
              <div class="input-group">
                <input type="search" v-model="phones.landline.phone_number" id="telephone" name="telephone" class="form-input" :class="classes" maxlength="15">
                <div class="input-group-append"><span class="input-group-text"><i class="fas fa-phone-alt"></i></span></div>
              </div>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Personal Email Address - Defaults to same as Primary/Website Login Email -->
            <ValidationProvider rules="email" v-slot="{errors, classes}" name="Personal Email Address" tag="div" class="col-xl-4 form-group">
              <label class="form-label" for="personal_email">Personal Email Address</label>
              <div class="input-group">
                <input type="search" v-model="emails.personal_email.email_address" id="personal_email" class="form-input" :class="classes">
                <div class="input-group-append"><span class="input-group-text"><i class="fas fa-at"></i></span></div>
              </div>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
          </div>
          <!-- Person Address -->
          <div class="row">
            <div class="col-12">
              <h5 class="font-weight-bold">Personal Address</h5>
            </div>
            <!-- Personal Address (Google Maps Search) -->
            <div class="col-xl-12 form-group">
              <label class="form-label" for="personal_address">Address (Google Maps Search)</label>
              <div class="input-group">
                <GmapAutocomplete :type="'search'" id="personal_address" name="personal_address" class="form-input input" placeholder="" @place_changed="set_personal_address"
                                  @input.native="watch_address" @keypress.enter="$event.preventDefault()"></GmapAutocomplete>
                <div class="input-group-append"><span class="input-group-text"><i class="fas fa-home"></i></span></div>
              </div>
            </div>
            <!-- Personal Address - Building -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_building">Building</label>
              <input type="search" v-model="addresses.home.building" id="personal_building" name="personal_building" class="form-input">
            </div>
            <!-- Personal Address - Street -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_street">Street</label>
              <input type="search" v-model="addresses.home.street" id="personal_street" name="personal_street" class="form-input">
            </div>
            <!-- Personal Address - Locality -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_locality">Locality</label>
              <input type="search" v-model="addresses.home.locality" id="personal_locality" name="personal_locality" class="form-input">
            </div>
            <!-- Personal Address - City / Town -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_city_town">City / Town</label>
              <input type="search" v-model="addresses.home.city_town" id="personal_city_town" name="personal_city_town" class="form-input">
            </div>
            <!-- Personal Address - County -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_county">County</label>
              <input type="search" v-model="addresses.home.county" id="personal_county" name="personal_county" class="form-input">
            </div>
            <!-- Personal Address - Postcode -->
            <div class="col-xl-4 form-group">
              <label class="form-label" for="personal_postcode">Postcode</label>
              <input type="search" v-model="addresses.home.postcode" id="personal_postcode" name="personal_postcode" class="form-input">
            </div>
          </div>
          <!-- Next of Kin Information -->
          <div class="row">
            <div class="col-12">
              <h5 class="font-weight-bold">Next of Kin Information</h5>
            </div>
            <!-- Next of Kin - Full Name -->
            <div class="col-xl-6 form-group">
              <label class="form-label" for="nok_name">Full Name</label>
              <input type="search" v-model="profile.nok_name" id="nok_name" name="nok_name" class="form-input" @input="forceCapitalizeApostrophe($event, profile, 'nok_name')">
            </div>
            <!-- Next of Kin - Contact Number -->
            <ValidationProvider rules="numeric|max:15" v-slot="{errors, classes}" name="Mobile" tag="div" class="col-xl-6 form-group">
              <label class="form-label" for="nok_number">Contact Number</label>
              <input type="search" v-model="profile.nok_contact_number" id="nok_number" name="nok_number" class="form-input" :class="classes" maxlength="15">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
          </div>
        </b-card>
      </div>
      <!-- Personal Bank Information -->
      <div class="col-xl-4">
        <b-card :header="employmentTypeStr() + ' Bank Information'" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <!-- Personal Bank Name -->
            <div class="col-12 form-group">
              <label class="form-label" for="bank_name">Bank Name</label>
              <input type="search" v-model="banks.personal.bank_name" id="bank_name" name="bank_name" class="form-input"
                     @input="forceUppercase($event, banks.personal, 'bank_name')">
            </div>
            <!-- Personal Bank Branch Location -->
            <div class="col-12 form-group">
              <label class="form-label" for="branch_location">Branch Location</label>
              <input type="search" v-model="banks.personal.branch_location" id="branch_location" name="branch_location" class="form-input"
                     @input="forceCapitalizeApostrophe($event, banks.personal, 'branch_location')">
            </div>

            <!-- Personal Bank Account Holder Name('s) -->
            <ValidationProvider :rules="getValidationRulesForBankPersonal() ? 'required' : ''" v-slot="{errors, classes}" name="Account Holder Name('s)" tag="div" class="col-12 form-group">
              <label class="form-label" for="accounts_holder">Account Holder Name('s)</label>
              <input type="search" v-model="banks.personal.accounts_holder" id="accounts_holder" name="accounts_holder" :class="['form-input', bank_account_fields, classes]" maxlength="255" @input="forceCapitalizeApostrophe($event, banks.personal, 'accounts_holder')">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>

            <!-- Personal Bank Account Number -->
            <ValidationProvider :rules="getValidationRulesForBankPersonal() ? 'required|digits:8' : ''" v-slot="{errors, classes}" name="Account Number" tag="div" class="col-12 form-group">
              <label class="form-label" for="account_number">Account Number</label>
              <input type="search" v-model="banks.personal.account_number" id="account_number" :class="['form-input', bank_account_fields, classes]" name="account_number" maxlength="8">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>

            <!-- Personal Bank Sort Code -->
            <ValidationProvider :rules="getValidationRulesForBankPersonal() ? 'required|digits:6' : ''" v-slot="{errors, classes}" name="Sort Code" tag="div" class="col-12 form-group">
              <label class="form-label" for="sort_code">Sort Code</label>
              <input type="search" v-model="banks.personal.sort_code" id="sort_code" :class="['form-input', bank_account_fields, classes]" name="sort_code" maxlength="6">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>


            <!-- Personal Bank Building Society Ref Number -->
            <div class="col-12 form-group">
              <label class="form-label" for="bsoc_ref">Building Society Ref Number</label>
              <input type="search" v-model="banks.personal.building_society_ref" id="bsoc_ref" name="bsoc_ref" class="form-input">
            </div>

              <!-- I have read back the details to the worker -->
              <div class="col-12 form-group">
                <div class="row">
                  <label class="col-xl-8 cardLabel" for="account_confirmed_user">I have read back the details to the worker.</label>
                  <div class="col-xl-4 form-group">
                    <bootstrap-toggle v-model="banks.personal.account_confirmed_user" id="account_confirmed_user" name="account_confirmed_user" :options="{ on: 'Yes', off: 'No' }"/>
                  </div>
                </div>
                
                <!-- show last confirmed -->
                <div v-if="banks.personal.account_confirmed_user_date" class="row">
                  <div class="col-12 form-group">
                    <div class="row">
                      <label class="col-xl-5 cardLabelText" for="account_confirmed_user"><b>Last confirmed details:</b></label>
                      <div class="col-xl-7 form-group">{{ banks.personal.account_confirmed_user_name }} @ {{ banks.personal.account_confirmed_user_date | removeSeconds }}</div>
                    </div>
                  </div>
                </div>
              </div>

          </div>
        </b-card>
      </div>
      <!-- Company / Sole Trader Information -->
      <div class="col-12" v-if="company_info.employment_type !== null && company_info.employment_type.id !== 33">
        <b-card :header="company_info.employment_type.value + ' Information'" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <!-- Self Employed UTR Number -->
            <ValidationProvider rules="digits:10" v-slot="{errors, classes}" name="Self Employed UTR Number" tag="div" class="col-xl-3 form-group">
              <label class="form-label" for="personal_utr_number">Self Employed UTR Number</label>
              <input type="search" v-model="profile.personal_utr_number" id="personal_utr_number" name="personal_utr_number" class="form-input" :class="classes" maxlength="10">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Trading Name -->
            <div class="col-xl-3 form-group">
              <label class="form-label" for="company_trading_name">Trading Name</label>
              <input type="search" v-model="company_info.company_trading_name" id="company_trading_name" name="company_trading_name" class="form-input"
                     @input="trading_name_input" v-if="company_info.employment_type.id === 32">
              <input type="search" v-model="company_info.company_trading_name" id="company_trading_name" name="company_trading_name" class="form-input" v-else>
              <div v-if="notEmptyObject(company_search_results)" class="companies_house_company_search" v-click-outside="clear_company_search_result">
                <ul>
                  <li v-for="company in company_search_results">
                    <button type="button" @click.prevent="select_company(company.company_number, company.company_status)"
                            :class="[company.company_status === 'dissolved' ? 'disabled' : 'clickable']" :disabled="disabledHMRC(company.company_status)">
                      <span class="c_status" :class="[company.company_status === 'active' ? 'active' : '']">{{ company.company_status }}</span>
                      <span class="c_title">{{ company.title }}</span>
                      <span class="c_number">{{ company.company_number }}</span>
                      <span class="c_address_snippet">{{ company.address_snippet }}</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div v-else-if="!notEmptyObject(company_search_results) && company_info.employment_type.id === 32 && searching_ch" class="companies_house_company_search">
                <ul>
                  <li>
                    <button :disabled="true">
                      <span class="c_title"><i class="fas fa-spinner fa-pulse"></i> &nbsp;Searching ...</span>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <!-- Company UTR Number -->
            <ValidationProvider rules="digits:10" v-slot="{errors, classes}" name="Company UTR Number" tag="div" class="form-group col-xl-3"
                                v-if="company_info.employment_type.id === 32">
              <label class="form-label" for="company_utr_number">Ltd Company UTR Number</label>
              <input type="search" v-model="company_info.company_utr_number" id="company_utr_number" name="company_utr_number" class="form-input" :class="classes" maxlength="10">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Limited Company LTD Number -->
            <ValidationProvider rules="ltdCompNoValidate" v-slot="{errors, classes}" name="LTD Reg Number" tag="div" class="form-group col-xl-3"
                                v-if="company_info.employment_type.id === 32">
              <label class="form-label" for="company_ltd_reg_number">LTD Registration Number</label>
              <input type="search" v-model="company_info.company_ltd_reg_number" id="company_ltd_reg_number" name="company_ltd_reg_number" class="form-input" :class="classes">
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
          </div>
          <!-- VAT NUMBER -->
          <div class="row">
            <div class="col-12">
              <h5 class="font-weight-bold">{{ company_info.employment_type.value }} VAT Registered</h5>
            </div>
            <!-- VAT Registered - Toggle -->
            <div class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="company_vat_registered">{{ company_info.employment_type.value }} UK VAT Registered?</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="company_info.vat_registered" id="company_vat_registered" name="company_vat_registered" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <!-- VAT Number - Input -->
            <ValidationProvider rules="VATregNoValidate|max:9" v-slot="{errors, classes}" name="VAT Number" tag="div" v-if="company_info.vat_registered"
                                class="col-xl-3 form-group">
              <label class="form-label" for="company_vat_number">VAT Number</label>
              <div class="input-group">
                <div class="input-group-prepend"><span class="input-group-text">GB</span></div>
                <input type="search" v-model="company_info.vat_number" id="company_vat_number" name="company_vat_number" class="form-input" :class="classes" maxlength="9">
              </div>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <div v-if="company_info.vat_registered" class="col-xl-3 form-group">
              <button type="button" class="btn orcaBtn grey_bg" id="check_vat_number" data-toggle="tooltip" title="Check VAT Number" @click.prevent="check_vat_number">
                CHECK VAT NUMBER
              </button>
            </div>
          </div>
          <!-- VAT Check Display -->
          <div class="row">
            <div class="col-xl-6 offset-xl-6" v-if="notEmptyObject(vat_check) && typeof vat_check !== 'string'">
              <div class="vat_check_display" :class="vat_check.target.name !== company_info.company_trading_name ? 'different' : ''">
                <div class="row">
                  <div class="col-2" v-if="vat_check.target.name !== company_info.company_trading_name">
                    <i class="fas fa-times"></i>
                  </div>
                  <div class="col-2" v-if="vat_check.target.name === company_info.company_trading_name">
                    <i class="fas fa-check"></i>
                  </div>
                  <div class="col-10">
                    <div>
                      <strong>Trading Name :</strong> <span class="vat_val">{{ vat_check.target.name }}</span>
                    </div>
                    <div>
                      <strong>Address :</strong> <span class="vat_val"><span v-for="(v, n) in vat_check.target.address" v-if="n !== 'countryCode'">{{
                        v
                      }}<span v-if="n !== 'postcode'">, </span></span></span>
                    </div>
                  </div>
                  <div class="col-10 offset-2 mt-2">
                    <p class="mb-0 font-italic">Please ensure that the address matches the address on record.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xl-6 offset-xl-6" v-if="notEmptyObject(vat_check) && typeof vat_check === 'string'">
              <div class="vat_check_display different">
                <div class="row">
                  <div class="col-2">
                    <i class="fas fa-times"></i>
                  </div>
                  <div class="col-10">
                    <div>
                      <strong>ISSUE :</strong> <span class="vat_val">{{ vat_check }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <!-- Company Address -->
            <div class="col-xl-6">
              <div class="row">
                <div class="col-12">
                  <h5 class="font-weight-bold">{{ company_info.employment_type.value }} Address</h5>
                </div>
                <!-- Company Address (Same as Personal) -->
                <div class="col-12">
                  <div class="row">
                    <label class="col-xl-8 cardLabel" for="company_address_same_as_personal">{{ company_info.employment_type.value }} address, same as personal?</label>
                    <div class="col-xl-4 form-group">
                      <bootstrap-toggle v-model="company_info.company_address_same_as_personal" id="company_address_same_as_personal" name="company_address_same_as_personal"
                                        :options="{ on: 'Yes', off: 'No' }"/>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!company_info.company_address_same_as_personal" class="row">
                <!-- Company Address Search -->
                <div class="col-12 form-group">
                  <label class="form-label" for="company_address">Address (Google Maps Search)</label>
                  <div class="input-group">
                    <GmapAutocomplete :type="'search'" id="company_address" name="company_address" class="form-input input" placeholder="" @place_changed="set_company_address"
                                      @input.native="watch_address" @keypress.enter="$event.preventDefault()"></GmapAutocomplete>
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-building"></i></span></div>
                  </div>
                </div>
                <!-- Company Address - Building -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_building">Building</label>
                  <input type="search" v-model="addresses.company.building" id="company_building" name="company_building" class="form-input">
                </div>
                <!-- Company Address - Street -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_street">Street</label>
                  <input type="search" v-model="addresses.company.street" id="company_street" name="company_street" class="form-input">
                </div>
                <!-- Company Address - Locality -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_locality">Locality</label>
                  <input type="search" v-model="addresses.company.locality" id="company_locality" name="company_locality" class="form-input">
                </div>
                <!-- Company Address - City / Town -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_city_town">City / Town</label>
                  <input type="search" v-model="addresses.company.city_town" id="company_city_town" name="company_city_town" class="form-input">
                </div>
                <!-- Company Address - County -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_county">County</label>
                  <input type="search" v-model="addresses.company.county" id="company_county" name="company_county" class="form-input">
                </div>
                <!-- Company Address - Postcode -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_postcode">Postcode</label>
                  <input type="search" v-model="addresses.company.postcode" id="company_postcode" name="company_postcode" class="form-input">
                </div>
              </div>
            </div>
            <!-- Company Bank Account -->
            <div class="col-xl-6">
              <div class="row">
                <div v-if="showAdditionalBank()" class="col-12">
                  <h5 class="font-weight-bold">{{ company_info.employment_type.value }} Bank Account</h5>
                </div>
                <!-- Company Bank Details -->
                <div v-if="showAdditionalBank()" class="col-12">
                  <div class="row">
                    <label class="col-xl-8 cardLabel" for="company_bank_same_as_personal">{{ company_info.employment_type.value }} Bank Account, same as personal</label>
                    <div class="col-xl-4 form-group">
                      <bootstrap-toggle v-model="company_info.company_bank_same_as_personal" id="company_bank_same_as_personal" name="company_bank_same_as_personal"
                                        :options="{ on: 'Yes', off: 'No' }"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row" v-if="!company_info.company_bank_same_as_personal">
                <!-- Company Bank Name -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_bank_name">Bank Name</label>
                  <input type="search" v-model="banks.company.bank_name" id="company_bank_name" name="company_bank_name" class="form-input"
                         @input="forceUppercase($event, banks.company, 'bank_name')">
                </div>
                <!-- Company Bank Branch Location -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_branch_location">Branch Location</label>
                  <input type="search" v-model="banks.company.branch_location" id="company_branch_location" name="company_branch_location" class="form-input"
                         @input="forceCapitalizeApostrophe($event, banks.company, 'branch_location')">
                </div>
                <!-- Company Bank Account Holder Name('s) -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_bank_accounts_holder">Account Holder Name('s)</label>
                  <input type="search" v-model="banks.company.accounts_holder" id="company_bank_accounts_holder" name="company_bank_accounts_holder"
                         class="form-input" @input="forceCapitalizeApostrophe($event, banks.company, 'accounts_holder')">
                </div>
                <!-- Company Bank Account Number -->
                <ValidationProvider rules="digits:8" v-slot="{errors, classes}" name="Account Number" tag="div" class="col-xl-4 form-group">
                  <label class="form-label" for="company_bank_account_number">Account Number</label>
                  <input type="search" v-model="banks.company.account_number" id="company_bank_account_number" :class="classes" name="company_bank_account_number"
                         class="form-input" maxlength="8">
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Company Bank Sort Code -->
                <ValidationProvider rules="digits:6" v-slot="{errors, classes}" name="Sort Code" tag="div" class="col-xl-4 form-group">
                  <label class="form-label" for="company_sort_code">Sort Code</label>
                  <input type="search" v-model="banks.company.sort_code" id="company_sort_code" :class="classes" name="company_sort_code" class="form-input"
                         maxlength="6">
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Company Bank Building Society Ref Number -->
                <div class="col-xl-4 form-group">
                  <label class="form-label" for="company_bsoc_ref">Building Society Ref Number</label>
                  <input type="search" v-model="banks.company.building_society_ref" id="company_bsoc_ref" name="company_bsoc_ref" class="form-input">
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <!-- Services -->
      <div class="col-12">
        <b-card header="Services" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <div class="col-12">
              <p class="font-weight-bold">Please indicate below which additional services the worker wishes to opt into:</p>
            </div>
            <!-- Personal Accident Insurance -->
            <div v-if="company_info.employment_type !== null && company_info.employment_type.id !== 33" class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="personal_accident_insurance">Personal Accident Insurance</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="services.personal_accident_insurance" id="personal_accident_insurance" name="personal_accident_insurance"
                                    :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <div v-if="services.personal_accident_insurance && company_info.employment_type !== null && company_info.employment_type.id !== 33" class="col-xl-6">
              <p class="font-weight-bold text-right cardLabel">An Additional charge of {{ pa_ins | currency }} is made per week</p>
            </div>
            <div class="col-xl-6" v-else></div>
            <!-- Genius Tax -->
            <div v-if="company_info.employment_type !== null && company_info.employment_type.id !== 33" class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="genius_tax">Genius Tax</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="services.genius_tax" id="genius_tax" name="genius_tax" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <div v-if="services.genius_tax && company_info.employment_type !== null && company_info.employment_type.id !== 33" class="col-xl-6">
              <p class="font-weight-bold text-right cardLabel">For a standard Self employed Tax Return £5.00 per week is taken</p>
            </div>
            <div class="col-xl-6" v-else></div>
            <!-- Recruitment/Resourcing -->
            <div class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="recruitment">Recruitment/Resourcing</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="services.recruitment" id="recruitment" name="recruitment" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <div v-if="services.recruitment" class="col-xl-6">
              <p class="font-weight-bold text-right cardLabel">Worker is open to being contacted regarding Recruitment/Resourcing opportunities</p>
            </div>
            <div class="col-xl-6" v-else></div>
          </div>
        </b-card>
      </div>
      <!-- Recruitment / Resourcing -->
      <div class="col-12" v-if="services.recruitment">
        <b-card header="Recruitment / Resourcing" header-tag="h4" body-class="mb-3 pb-2">
          <div class="row">
            <div class="col-12">
              <p class="font-weight-bold">The following information relates to future Resourcing and Recruitment placement opportunities for the worker.</p>
            </div>
            <!-- Placement Types -->
            <div class="col-lg-2 form-group">
              <label class="form-label" for="job_types">Placement Types</label>
              <multiselect v-model="recruitment.job_types" id="job_types" name="job_types" placeholder="" :options="ms_opts.job_types" label="value" :show-labels="false"
                           track-by="id" :multiple="true"></multiselect>
            </div>
            <!-- Industries -->
            <div class="col-lg-5 form-group">
              <label class="form-label" for="industries">Industries</label>
              <multiselect v-model="recruitment.industries" id="industries" name="job_types" placeholder="" :options="ms_opts.sectors" label="sector" :show-labels="false"
                           @select="select_industry" track-by="id" :multiple="true"></multiselect>
            </div>
            <!-- Job Titles -->
            <div class="col-lg-5 form-group job_title_multiselect">
              <label class="form-label" for="job_titles">Potential Job Titles</label>
              <multiselect v-model="recruitment.job_titles" placeholder="" id="job_titles" track-by="id" :options="ms_opts.rec_job_titles"
                           :option-height="200" :custom-label="job_title_label" :show-labels="false" :multiple="true"
                           :disabled="recruitment.industries.length === 0">
                <template slot="tag" slot-scope="{option, remove}">
                  <span class="multiselect__tag">
                    <span>{{ option.job_title }}</span><i aria-hidden="true" tabindex="1" class="multiselect__tag-icon" @click.prevent="remove(option)"></i>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <span class="option__title">{{ props.option.job_title }}</span>
                    <span class="option__small">{{ props.option.sector_name }}</span>
                  </div>
                </template>
              </multiselect>
            </div>
            <!-- Health & Disabilities -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">Health &amp; Disabilities</h5>
            </div>
            <!-- Any Health/Disabilities -->
            <div class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="any_health_disability">Any Health problems or disabilities?</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="recruitment.any_health_disability" id="any_health_disability" name="any_health_disability" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <div class="col-xl-6"></div>
            <!-- Health/Disability Detail -->
            <ValidationProvider rules="" v-slot="{errors, classes}" name="Health/Disability Detail" tag="div"
                                v-if="recruitment.any_health_disability" class="col-xl-6 form-group">
              <label class="form-label" for="health_disability_details">Health/Disability Detail</label>
              <textarea type="search" v-model="recruitment.health_disability_details" id="health_disability_details"
                        name="health_disability_details" class="form-input" :class="classes" rows="3"></textarea>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Special Requirements -->
            <ValidationProvider rules="" v-slot="{errors, classes}" name="Special Requirements" tag="div" v-if="recruitment.any_health_disability"
                                class="col-xl-6 form-group">
              <label class="form-label" for="health_disability_special_requirements">Special Requirements</label>
              <textarea type="search" v-model="recruitment.health_disability_special_requirements" id="health_disability_special_requirements"
                        name="health_disability_special_requirements" class="form-input" :class="classes" rows="3"></textarea>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Travel -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">Travel</h5>
            </div>
            <!-- Have own transport -->
            <div class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="have_own_transport">Have own transport?</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="recruitment.have_own_transport" id="have_own_transport" name="have_own_transport" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <!-- Distance willing to travel -->
            <div class="col-xl-6 form-group">
              <label class="form-label" for="distance_willing_to_travel">Distance willing to travel</label>
              <input type="search" v-model="recruitment.distance_willing_to_travel" id="distance_willing_to_travel" name="distance_willing_to_travel" class="form-input">
            </div>
            <!-- Criminal Convictions -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">Criminal Convictions</h5>
            </div>
            <!-- Any criminal convictions -->
            <div class="col-xl-6">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="any_criminal_convictions">Have any past or pending Criminal Convictions?</label>
                <div class="col-xl-4 form-group">
                  <bootstrap-toggle v-model="recruitment.any_criminal_convictions" id="any_criminal_convictions" name="any_criminal_convictions"
                                    :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>
            <!-- criminal conviction details -->
            <ValidationProvider :rules="{required : recruitment.any_criminal_convictions}" v-slot="{errors, classes}" name="Criminal Conviction(s) Detail" tag="div"
                                v-if="recruitment.any_criminal_convictions" class="col-xl-6 form-group">
              <label class="form-label" for="criminal_convictions_details">Criminal Conviction(s) Detail</label>
              <textarea type="search" v-model="recruitment.criminal_convictions_details" id="criminal_convictions_details"
                        name="criminal_convictions_details" class="form-input" :class="classes" rows="3"></textarea>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Eduction/Qualifications -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">Eduction/Qualifications</h5>
            </div>
            <!-- Education -->
            <ValidationProvider rules="" v-slot="{errors, classes}" name="Education" tag="div" class="col-xl-6 form-group">
              <label class="form-label" for="education">Education</label>
              <textarea type="search" v-model="recruitment.education" id="education" name="education" class="form-input" :class="classes" rows="3"></textarea>
              <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
            </ValidationProvider>
            <!-- Qualifications -->
            <div class="col-xl-6 form-group">
              <label class="form-label" for="job_types">Qualifications</label>
              <multiselect v-model="recruitment.qualifications" id="qualifications" name="qualifications" placeholder="" :options="ms_opts.qualifications" label="qualification"
                           :show-labels="false" track-by="id" :multiple="true"></multiselect>
            </div>
            <!-- Previous Employment -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">Previous Employment</h5>
            </div>
            <!-- Employer 1 -->
            <div class="col-xl-6">
              <p class="font-weight-bold">Employer 1</p>
              <div class="row ml-0 mr-0">
                <!-- Previous Employer - Company Name -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_company">Company Name</label>
                  <input type="search" v-model="recruitment.prev_emp_1_company" id="prev_emp_1_company" name="prev_emp_1_company" class="form-input">
                </div>
                <!-- Previous Employer - Company Address -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_address">Company Address</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.prev_emp_1_address" id="prev_emp_1_address" name="prev_emp_1_address" class="form-input">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-building"></i></span></div>
                  </div>
                </div>
                <!-- Previous Employer - Start Date -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Emp 1 Start Date" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_start_date">Start Date</label>
                  <datepicker v-model="recruitment.prev_emp_1_start_date" id="prev_emp_1_start_date" name="prev_emp_1_start_date" :input-class="['date-input', classes]"
                              :format="dateFormat" :clear-button="true" clear-button-icon="far fa-calendar-times" @input="select_emp_1_start_date" :use-utc="true"></datepicker>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Previous Employer - End Date -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Emp 1 End Date" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_start_date">End Date</label>
                  <datepicker v-model="recruitment.prev_emp_1_end_date" id="prev_emp_1_end_date" name="prev_emp_1_end_date" :input-class="['date-input', classes]"
                              :format="dateFormat" :clear-button="true" clear-button-icon="far fa-calendar-times" @input="select_emp_1_end_date" :use-utc="true"></datepicker>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Previous Employer - Position -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_position">Position</label>
                  <input type="search" v-model="recruitment.prev_emp_1_position" id="prev_emp_1_position" name="prev_emp_1_position" class="form-input">
                </div>
                <!-- Previous Employer - Salary/Wage -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_1_salary">Salary/Wage</label>
                  <input type="search" v-model="recruitment.prev_emp_1_salary" id="prev_emp_1_salary" name="prev_emp_1_salary" class="form-input">
                </div>
                <!-- Previous Employer - Reason for leaving -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Reason for leaving" tag="div" class="col-12 form-group">
                  <label class="form-label" for="prev_emp_1_reason_for_leaving">Reason for leaving</label>
                  <textarea type="search" v-model="recruitment.prev_emp_1_reason_for_leaving" id="prev_emp_1_reason_for_leaving" name="prev_emp_1_reason_for_leaving"
                            class="form-input" :class="classes" rows="3"></textarea>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </div>
            <!-- Employer 2 -->
            <div class="col-xl-6">
              <p class="font-weight-bold">Employer 2</p>
              <div class="row ml-0 mr-0">
                <!-- Previous Employer - Company Name -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_company">Company Name</label>
                  <input type="search" v-model="recruitment.prev_emp_2_company" id="prev_emp_2_company" name="prev_emp_2_company" class="form-input">
                </div>
                <!-- Previous Employer - Company Address -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_address">Company Address</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.prev_emp_2_address" id="prev_emp_2_address" name="prev_emp_2_address" class="form-input">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-building"></i></span></div>
                  </div>
                </div>
                <!-- Previous Employer - Start Date -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Emp 2 Start Date" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_start_date">Start Date</label>
                  <datepicker v-model="recruitment.prev_emp_2_start_date" id="prev_emp_2_start_date" name="prev_emp_2_start_date" :input-class="['date-input', classes]"
                              :format="dateFormat" :clear-button="true" clear-button-icon="far fa-calendar-times" @input="select_emp_2_start_date" :use-utc="true"></datepicker>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Previous Employer - End Date -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Emp 2 End Date" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_end_date">End Date</label>
                  <datepicker v-model="recruitment.prev_emp_2_end_date" id="prev_emp_2_end_date" name="prev_emp_2_end_date" :input-class="['date-input', classes]"
                              :format="dateFormat" :clear-button="true" clear-button-icon="far fa-calendar-times" @input="select_emp_2_end_date" :use-utc="true"></datepicker>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Previous Employer - Position -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_position">Position</label>
                  <input type="search" v-model="recruitment.prev_emp_2_position" id="prev_emp_2_position" name="prev_emp_2_position" class="form-input">
                </div>
                <!-- Previous Employer - Salary/Wage -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="prev_emp_2_salary">Salary/Wage</label>
                  <input type="search" v-model="recruitment.prev_emp_2_salary" id="prev_emp_2_salary" name="prev_emp_2_salary" class="form-input">
                </div>
                <!-- Previous Employer - Reason for leaving -->
                <ValidationProvider rules="" v-slot="{errors, classes}" name="Reason for leaving" tag="div" class="col-12 form-group">
                  <label class="form-label" for="prev_emp_2_reason_for_leaving">Reason for leaving</label>
                  <textarea type="search" v-model="recruitment.prev_emp_2_reason_for_leaving" id="prev_emp_2_reason_for_leaving" name="prev_emp_2_reason_for_leaving"
                            class="form-input" :class="classes" rows="3"></textarea>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </div>
            <!-- References -->
            <div class="col-12">
              <hr>
              <h5 class="font-weight-bold">References</h5>
            </div>
            <!-- Reference 1 -->
            <div class="col-xl-6">
              <p class="font-weight-bold">Reference 1</p>
              <div class="row ml-0 mr-0">
                <!-- Name -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_1_name">Name</label>
                  <input type="search" v-model="recruitment.reference_1_name" id="reference_1_name" name="reference_1_name" class="form-input">
                </div>
                <!-- Relationship -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_1_company_relationship">Relationship</label>
                  <input type="search" v-model="recruitment.reference_1_company_relationship" id="reference_1_company_relationship" name="reference_1_company_relationship"
                         class="form-input">
                </div>
                <!-- Telephone -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_1_telephone">Telephone</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.reference_1_telephone" id="reference_1_telephone" name="reference_1_telephone" class="form-input">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-phone-alt"></i></span></div>
                  </div>
                </div>
                <!-- Email -->
                <ValidationProvider rules="email" v-slot="{errors, classes}" name="Reference 1 Email Address" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="reference_1_email">Email</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.reference_1_email" id="reference_1_email" name="reference_1_email" class="form-input" :class="classes">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-at"></i></span></div>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </div>
            <!-- Reference 2 -->
            <div class="col-xl-6">
              <p class="font-weight-bold">Reference 2</p>
              <div class="row ml-0 mr-0">
                <!-- Name -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_2_name">Name</label>
                  <input type="search" v-model="recruitment.reference_2_name" id="reference_2_name" name="reference_2_name" class="form-input">
                </div>
                <!-- Relationship -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_2_company_relationship">Relationship</label>
                  <input type="search" v-model="recruitment.reference_2_company_relationship" id="reference_2_company_relationship" name="reference_2_company_relationship"
                         class="form-input">
                </div>
                <!-- Telephone -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="reference_2_telephone">Telephone</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.reference_2_telephone" id="reference_2_telephone" name="reference_2_telephone" class="form-input">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-phone-alt"></i></span></div>
                  </div>
                </div>
                <!-- Email -->
                <ValidationProvider rules="email" v-slot="{errors, classes}" name="Reference 2 Email Address" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="reference_2_email">Email</label>
                  <div class="input-group">
                    <input type="search" v-model="recruitment.reference_2_email" id="reference_2_email" name="reference_2_email" class="form-input" :class="classes">
                    <div class="input-group-append"><span class="input-group-text"><i class="fas fa-at"></i></span></div>
                  </div>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <!-- WELCOME EMAIL INFO - if like '@orca-no-email.com' -->
      <div class="col-12" v-if="emails.website_login.email_address.includes('@orca-no-email.com')">
        <div class="no_email_warning">
          <h4 class="text-uppercase"><i class="fas fa-exclamation-triangle"></i> No Valid Email Address Warning <i class="fas fa-exclamation-triangle"></i></h4>
          <p class="mb-0">Due to this worker not having an email address / not knowing their email address, <strong>No welcome email will be sent</strong>!</p>
          <p class="mb-0">There will be an option to send a welcome email upon updating their record with a valid email address.</p>
          <!-- TODO -- ADD TO DB - sent welcome email -->
        </div>
      </div>
      <!-- BUTTON -->
      <div class="col-12" v-if="company_info.employment_type !== null && company_info.employment_type.id !== 33">
        <button type="submit" class="btn orcaBtn w-100" :disabled="saving">
          <span v-if="saving" class="btnLoading"><i class="fas fa-spinner fa-pulse"></i>Saving Worker ...</span>
          <span v-else>Register Worker<span v-if="!emails.website_login.email_address.includes('@orca-no-email.com')"> - Send Welcome Email</span> - Proceed to Entity Test</span>
        </button>
      </div>
      <div class="col-12" v-if="company_info.employment_type !== null && company_info.employment_type.id === 33">
        <button type="submit" class="btn orcaBtn w-100" :disabled="saving">
          <span v-if="saving" class="btnLoading"><i class="fas fa-spinner fa-pulse"></i>Saving Worker ...</span>
          <span v-else>Register Worker<span v-if="!emails.website_login.email_address.includes('@orca-no-email.com')"> - Send Welcome Email</span></span>
        </button>
      </div>
    </form>
  </ValidationObserver>
</template>
<script>
import Multiselect from 'vue-multiselect';
import Datepicker from 'vuejs-datepicker';
import BootstrapToggle from 'vue-bootstrap-toggle';
import * as GmapVue from 'gmap-vue'
import Vue from 'vue';

Vue.use(GmapVue, {
  load: {
    key: process.env.MIX_API_GMAPS_KEY, // LIVE
    libraries: 'places',
    region: 'GB'
  }
});

export default {
  name: 'register_worker',
  components: {Multiselect, Datepicker, BootstrapToggle},
  data() {
    return {
      saving: false,
      // Set Genius PA Insurance value from env file
      pa_ins: process.env.MIX_PA_INS,
      // All worker ni numbers in crm - to check if ni number entered exists already
      all_worker_ni_numbers: {},
      // All worker emails in crm - to check if email entered exists already
      all_worker_emails: {},
      // current signed in crm user details
      user: this.$store.state.auth.me,

      /**
       * Multi-Select Options
       */
      ms_opts: {
        titles: this.$store.state.crm_options.person_title,
        nationalities: this.$store.state.crm_options.nationalities,
        sectors: this.$store.state.crm_options.sectors,
        job_titles_NULL: [],
        job_titles: this.$store.state.crm_options.job_titles,
        rec_temp_job_titles: this.$store.state.crm_options.job_titles,
        rec_job_titles: [],
        employment_types: this.$store.state.crm_options.worker_employment_type,
        job_types: this.$store.state.crm_options.worker_recruitment_job_types,
        qualifications: this.$store.state.crm_options.qualifications
      },

      /**
       * Worker Fields
       */
      banks: {
        personal: {
          worker_id: null,
          bank_type_id: 39,
          bank_name: '',
          branch_location: '',
          accounts_holder: '',
          account_number: '',
          sort_code: '',
          building_society_ref: '',
          account_confirmed_user: false,
          account_confirmed_user_id: '',
          account_confirmed_user_name: '',
          account_confirmed_user_date: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        },
        company: {
          worker_id: null,
          bank_type_id: 40,
          bank_name: '',
          branch_location: '',
          accounts_holder: '',
          accounts_number: '',
          sort_code: '',
          building_society_ref: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        }
      },
      company_info: {
        worker_id: null,
        company_internal_finance_ref: null,
        company_external_finance_ref: null,
        employment_type: null,
        employment_type_id: null,
        company_trading_name: '',
        company_utr_number: '',
        vat_registered: false,
        vat_number: '',
        company_ltd_reg_number: '',
        company_address_same_as_personal: true,
        company_address_id: null,
        company_bank_same_as_personal: true,
        company_bank_id: null,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      profile: {
        worker_id: null,
        job_title: null,
        job_title_id: null,
        personal_address_id: null,
        ni_number: '',
        personal_utr_number: '',
        nok_name: '',
        nok_contact_number: '',
        primary_bank_id: null,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      person: {
        title_id: null,
        title: null,
        first_name: '',
        surname: '',
        dob: '',
        nationality_id: null,
        nationality: null,
        is_uk_citizen: false,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      worker: {
        internal_finance_ref: null,
        person_id: null,
        welcome_email_sent_date: null,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      services: {
        worker_id: null,
        recruitment: true,
        personal_accident_insurance: false,
        genius_tax: false,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      recruitment: {
        worker_id: null,
        job_types: null,
        industries: [],
        job_titles: null,
        any_health_disability: false,
        health_disability_details: '',
        health_disability_special_requirements: '',
        have_own_transport: false,
        distance_willing_to_travel: '',
        any_criminal_convictions: false,
        criminal_convictions_details: '',
        education: '',
        qualifications: null,
        prev_emp_1_company: '',
        prev_emp_1_address: '',
        prev_emp_1_start_date: null,
        prev_emp_1_start_date_disabled: {
          to: '',
          from: ''
        },
        prev_emp_1_end_date: null,
        prev_emp_1_end_date_disabled: {
          to: '',
          from: '',
        },
        prev_emp_1_position: '',
        prev_emp_1_reason_for_leaving: '',
        prev_emp_1_salary: '',
        prev_emp_2_company: '',
        prev_emp_2_address: '',
        prev_emp_2_start_date: null,
        prev_emp_2_start_date_disabled: {
          to: '',
          from: ''
        },
        prev_emp_2_end_date: null,
        prev_emp_2_end_date_disabled: {
          to: '',
          from: '',
        },
        prev_emp_2_position: '',
        prev_emp_2_reason_for_leaving: '',
        prev_emp_2_salary: '',
        reference_1_name: '',
        reference_1_company_relationship: '',
        reference_1_telephone: '',
        reference_1_email: '',
        reference_2_name: '',
        reference_2_company_relationship: '',
        reference_2_telephone: '',
        reference_2_email: '',
        immigration_permission: false,
        agree_48_hour_opt_out: false,
        agree_equal_op: false,
        agree_dpa: false,
        agree_contract_services: false,
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },
      addresses: {
        home: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Address',
          belongs_to_id: null,
          address_type_id: 47,
          building: '',
          street: '',
          locality: '',
          city_town: '',
          county: '',
          postcode: '',
          full: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        },
        company: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Address',
          belongs_to_id: null,
          address_type_id: 106,
          building: '',
          street: '',
          locality: '',
          city_town: '',
          county: '',
          postcode: '',
          full: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        }
      },
      phones: {
        mobile: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Phone',
          belongs_to_id: null,
          phone_type_id: 59,
          phone_number: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        },
        landline: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Phone',
          belongs_to_id: null,
          phone_type_id: 58,
          phone_number: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        }
      },

      create_website_account: false,
      bank_account_fields: '',

      emails: {
        website_login: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Email',
          belongs_to_id: null,
          email_type_id: 67,
          email_address: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        },
        personal_email: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Email',
          belongs_to_id: null,
          email_type_id: 64,
          email_address: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        },
        business_email: {
          worker_id: null,
          belongs_to_type: 'App\\Models\\Workers\\Worker',
          contact_info_model_type: 'App\\Models\\Contact_Info\\Email',
          belongs_to_id: null,
          email_type_id: 66,
          email_address: '',
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        }
      },
      person_place_full: null,
      company_place_full: null,
      searching_ch: false,
      company_search_results: {},
      awaitingTradingNameSearch: false,
      // vat number check data
      vat_check: {},
    }
  },
  beforeMount() {
    /**
     * If Payroll CRM Type -> default recruitment worker services to false
     * -------------------------------
     */
    if (this.crmType === 'payroll') {
      this.services.recruitment = false;
      this.create_website_account = true;
    } else {
      this.create_website_account = false;
    }
  },
  computed: {
    /**
     * PERSONAL INFORMATION
     * ========================================================================================================================
     */
    /**
     * Check if nationality value in the UK or Republic of Ireland
     * -------------------------------
     */
    check_uk_ireland() {
      if (this.person.is_uk_citizen) {
        return true;
      } else {
        let uk_ireland = ['United Kingdom (UK)', 'Wales', 'Scotland', 'Northern Ireland', 'England', 'Republic of Ireland'];
        return this.person.nationality !== null && uk_ireland.includes(this.person.nationality.country_name);
      }
    },
    /**
     * Check if nationality value in the EU
     * -------------------------------
     */
    check_eu() {
      if (this.person.is_uk_citizen) {
        return this.check_uk_ireland();
      } else {
        let eu = ['Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece',
          'Hungary', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'The Netherlands', 'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden']
        return this.person.nationality !== null && eu.includes(this.person.nationality.country_name)
      }
    },
    /**
     * Check if nationality value is international (not UK & RoI or EU)
     * -------------------------------
     */
    check_international() {
      if (this.person.is_uk_citizen) {
        return this.check_uk_ireland();
      } else {
        return this.person.nationality !== null
      }
    },
  },
  methods: {
    getValidationRulesForBankPersonal() {
      const { accounts_holder, account_number, sort_code } = this.banks.personal;
      const isAnyFieldFilled =  accounts_holder || account_number || sort_code;

      // for green color in fields
      if (isAnyFieldFilled) {
        this.bank_account_fields = 'requiredField';
      } else {
        this.bank_account_fields = '';
      }

      return isAnyFieldFilled;
    },
    /**
     * FORM
     * ========================================================================================================================
     */
    /**
     * Date Format for datepicker
     * -------------------------------
     */
    dateFormat(date) {
      return this.$moment(date).format("DD/MM/YYYY");
    },
    /**
     * Personal & Company Address Google Search Function
     * -------------------------------
     * Clear address data values if google search input is cleared
     */
    watch_address(place) {
      if (place.path[0].value === '') {
        if (place.path[0].id === 'personal_address') {
          this.person_place_full = null;
          this.addresses.home.building = null;
          this.addresses.home.street = null;
          this.addresses.home.locality = null;
          this.addresses.home.city_town = null;
          this.addresses.home.county = null;
          this.addresses.home.postcode = null;
        }
        if (place.path[0].id === 'company_address') {
          this.company_place_full = null;
          this.addresses.company.building = null;
          this.addresses.company.street = null;
          this.addresses.company.locality = null;
          this.addresses.company.city_town = null;
          this.addresses.company.county = null;
          this.addresses.company.postcode = null;
        }
      }
    },
    /**
     * Submit Registration of worker form
     * -------------------------------
     */
    register_worker_submit() {
      this.$refs.registerWorkerForm.validate().then(success => {
        if (!success) {
          this.formErrorsValidationScroll(this.$refs.registerWorkerForm);
        }
        else {
          this.saving = true;
          // Set title id
          if (this.person.title !== null && Object.keys(this.person.title).length > 1) {
            this.person.title_id = this.person.title.id;
          }
          // Set nationality id
          if (this.person.nationality !== null) {
            this.person.nationality_id = this.person.nationality.id;
          }
          // Save Person
          axios.post(this.crmApiPath + 'person', this.person).then(person_id => {
            // set worker -> person_id
            this.worker.person_id = person_id.data;
            // if not no email -> set welcome email sent date -- later use this same if to send email or not
            if (!this.emails.website_login.email_address.includes('@orca-no-email.com')) {
              this.worker.welcome_email_sent_date = this.$moment().format('YYYY-MM-DD HH:mm:ss');
            }
            // save worker
            axios.post(this.crmApiPath + 'worker', this.worker).then(worker_id => {
              let worker_promises = [];
              // Worker Profile
              worker_promises.push(new Promise(resolve => {
                // set worker_id
                this.profile.worker_id = worker_id.data
                // set job_title_id
                if (this.profile.job_title !== null) {
                  this.profile.job_title_id = this.profile.job_title.id;
                }
                // save worker_profile
                axios.post(this.crmApiPath + 'worker/profile', this.profile).then(() => resolve())
                    .catch(error => console.log(error));
              }))
              // Worker Company Info
              worker_promises.push(new Promise(resolve => {
                // set worker_id
                this.company_info.worker_id = worker_id.data;
                // set employment_type_id
                if (this.company_info.employment_type !== null) {
                  this.company_info.employment_type_id = this.company_info.employment_type.id;
                }
                // save worker_company_info
                axios.post(this.crmApiPath + 'worker/company_info', this.company_info).then(() => resolve())
                    .catch(error => console.log(error));
              }))
              // Worker Services
              worker_promises.push(new Promise(resolve => {
                // set worker_id
                this.services.worker_id = worker_id.data;
                // save worker_services
                axios.post(this.crmApiPath + 'worker/services', this.services).then(() => resolve())
                    .catch(error => console.log(error));
              }))
              // Personal Bank
              worker_promises.push(new Promise(resolve => {
                // set worker_id
                this.banks.personal.worker_id = worker_id.data;
                // save personal worker_bank
                axios.post(this.crmApiPath + 'worker/bank', this.banks.personal).then(() => resolve())
                    .catch(error => console.log(error));
              }))
              // Company Bank (if Sole trader or limited)
              if (this.company_info.employment_type.id === 29 || this.company_info.employment_type.id === 30 || this.company_info.employment_type.id === 32) {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.banks.company.worker_id = worker_id.data;
                  // save company worker_bank
                  axios.post(this.crmApiPath + 'worker/bank', this.banks.company).then(() => resolve())
                      .catch(error => console.log(error));
                }))
              }
              // Personal Home Address
              this.addresses.home.full = this.person_place_full;
              if (this.addresses.home.full !== '' || this.addresses.home.full !== null) {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.addresses.home.worker_id = worker_id.data;
                  // set belongs_to_id (worker_id)
                  this.addresses.home.belongs_to_id = worker_id.data;
                  // save personal address
                  axios.post(this.crmApiPath + 'contact_info/address', this.addresses.home).then(() => resolve())
                      .catch(error => console.log(error));
                }))
              }
              // Company Address
              this.addresses.company.full = this.company_place_full;
              if ((this.addresses.company.full !== '' || this.addresses.company.full !== null) &&
                  (this.company_info.employment_type.id === 29 || this.company_info.employment_type.id === 30 || this.company_info.employment_type.id === 32)) {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.addresses.company.worker_id = worker_id.data;
                  // set belongs_to_id (worker_id)
                  this.addresses.company.belongs_to_id = worker_id.data;
                  // save company address
                  axios.post(this.crmApiPath + 'contact_info/address', this.addresses.company).then(() => resolve())
                      .catch(error => console.log(error));
                }))
              }
              // Mobile Phone
              if (this.phones.mobile.phone_number !== '') {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.phones.mobile.worker_id = worker_id.data;
                  // set belongs_to_id (worker_id)
                  this.phones.mobile.belongs_to_id = worker_id.data;
                  // save personal mobile
                  axios.post(this.crmApiPath + 'contact_info/phone', this.phones.mobile).then(() => resolve())
                      .catch(error => console.log(error));
                }))
              }
              // Landline Phone
              if (this.phones.landline.phone_number !== '') {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.phones.landline.worker_id = worker_id.data;
                  // set belongs_to_id (worker_id)
                  this.phones.landline.belongs_to_id = worker_id.data;
                  // save personal landline
                  axios.post(this.crmApiPath + 'contact_info/phone', this.phones.landline)
                      .then(() => resolve()).catch(error => console.log(error));
                }))
              }
              // Email primary/website login (Worker Login Type)
              worker_promises.push(new Promise(resolve => {
                // set worker_id
                this.emails.website_login.worker_id = worker_id.data;
                // set belongs_to_id (worker_id)
                this.emails.website_login.belongs_to_id = worker_id.data;
                // save primary/website login email
                axios.post(this.crmApiPath + 'contact_info/email', this.emails.website_login)
                    .then(() => resolve()).catch(error => console.log(error));
              }))
              // Email (personal)
              if (this.emails.personal_email.email_address !== '') {
                worker_promises.push(new Promise(resolve => {
                  // set worker_id
                  this.emails.personal_email.worker_id = worker_id.data;
                  // set belongs_to_id (worker_id)
                  this.emails.personal_email.belongs_to_id = worker_id.data;
                  // save primary/website login email
                  axios.post(this.crmApiPath + 'contact_info/email', this.emails.personal_email)
                      .then(() => resolve()).catch(error => console.log(error));
                }))
              }
              // Worker Recruitment
              if (this.services.recruitment) {
                // set worker_id
                this.recruitment.worker_id = worker_id.data;
                // Stringify arrays
                this.recruitment.job_types = JSON.stringify(this.recruitment.job_types);
                this.recruitment.industries = JSON.stringify(this.recruitment.industries);
                this.recruitment.job_titles = JSON.stringify(this.recruitment.job_titles);
                this.recruitment.qualifications = JSON.stringify(this.recruitment.qualifications);
                // save recruitment_info
                worker_promises.push(new Promise(resolve => {
                  axios.post(this.crmApiPath + 'worker/recruitment_info', this.recruitment).then(() => resolve()).catch(error => console.log(error));
                }))
              }
              // VAT Verification
              if (this.company_info.vat_registered && this.notEmptyObject(this.vat_check) && typeof this.vat_check !== 'string') {
                let temp = {
                  company_id: null,
                  worker_id: worker_id.data,
                  name: this.vat_check.target.name,
                  vat_number: this.vat_check.target.vatNumber,
                  address: JSON.stringify(this.vat_check.target.address),
                  processing_date: this.vat_check.processingDate,
                  log_by_type: 'App\\Models\\Users\\User',
                  log_by_id: this.$store.state.auth.me.id
                }
                worker_promises.push(new Promise(resolve => {
                  // save vat verification
                  axios.post(this.crmApiPath + 'hmrc/vat/store', temp)
                      .then(() => resolve()).catch(error => console.log(error));
                }))
              }
              // Perform Promise resolution
              Promise.all(worker_promises).then(() => {
                // send registration welcome email (if not temp address)
                if (!this.emails.website_login.email_address.includes('@orca-no-email.com')) {

                  let password = '';
                  let create_website_account_promises = [];
                  
                  if (this.crmType === 'payroll' && this.create_website_account === true) {
                    create_website_account_promises.push(new Promise(resolve => {
                      axios.post(this.crmApiPath + 'worker/create_web_user', {
                        'worker_id': worker_id.data,
                        'employment_type_id': this.company_info.employment_type.id,
                        'first_name': this.person.first_name,
                        'surname': this.person.surname,
                        'email': this.emails.website_login.email_address,
                      }).then(res => {
                        this.$snotify.success('Create web account');
                        password = res.data.password;
                        resolve();
                      }).catch(error => console.log(error));
                    }));
                  }

                  Promise.all(create_website_account_promises).then(() => {
                    axios.post(this.crmApiPath + 'worker/email/registration', {
                      'first_name': this.person.first_name,
                      'surname': this.person.surname,
                      'email': this.emails.website_login.email_address,
                      'employment_type': this.company_info.employment_type.value,
                      'create_website_account': this.create_website_account,
                      'password': password,
                    }).then(() => {
                      this.$snotify.success('Worker Registered');
                      this.$bus.$emit('table::reload', {table: 'all_workers', type: 'store'});
                      // if not PAYE type then proceed to entity test -> else go to worker profile
                      if (this.company_info.employment_type !== 33) {
                        // commit new worker crm id to store for use in new entity test
                        this.$store.commit('CURRENT_NEW_ENTITY_TEST_WORKER_ID', worker_id.data);
                        // Close current tab and open up new entity test for worker
                        this.$nextTick(() => {
                          this.saving = false;
                          this.$tabs.close({
                            path: this.$router.currentRoute.fullPath,
                            to: '/workers/entity_test/new'
                          });
                        })
                      }
                      else {
                        // Close current tab and open up new entity test for worker
                        this.$nextTick(() => {
                          this.saving = false;
                          this.$tabs.close({
                            path: this.$router.currentRoute.fullPath,
                            to: '/workers/profile'
                          });
                          this.$tabs.refresh('/workers/profile', true);
                        })
                      }

                    }).catch(error => console.log(error));
                  });
                }
                else {
                  this.$snotify.success('Worker Registered');
                  this.$bus.$emit('table::reload', {table: 'all_workers', type: 'store'});
                  // Close current tab and open up new entity test for worker
                  if (this.company_info.employment_type !== 33) {
                    // commit new worker crm id to store for use in new entity test
                    this.$store.commit('CURRENT_NEW_ENTITY_TEST_WORKER_ID', worker_id.data);
                    this.$nextTick(() => {
                      this.saving = false;
                      this.$tabs.close({
                        path: this.$router.currentRoute.fullPath,
                        to: '/workers/entity_test/new'
                      });
                    })
                  }
                  else {
                    this.$nextTick(() => {
                      this.saving = false;
                      this.$tabs.close({
                        path: this.$router.currentRoute.fullPath,
                        to: '/workers/profile'
                      });
                      this.$tabs.refresh('/workers/profile', true);
                    })
                  }
                }


              })
            })
          })
        }
      });
    },
    /**
     * PERSONAL INFORMATION
     * ========================================================================================================================
     */
    /**
     * Format DOB date picked
     * -------------------------------
     */
    select_dob(selected_dob) {
      if (selected_dob !== null) {
        this.person.dob = this.$moment(selected_dob).format('YYYY-MM-DD');
      }
    },
    /**
     *  NI Check
     *  -------------------------------
     *  Check if national insurance number inputted exists already in orca or not
     *  If it does -> popup to redirect to profile of worker
     */
    check_if_ni_exists(ni_num) {
      if (ni_num !== "") {
        axios.post(this.crmApiPath + 'worker/existing/ni_number', {ni_number: ni_num}).then(res => {
          if (res.data !== "") {
            this.$snotify.confirm('', '', {
              closeOnClick: false,
              position: 'centerCenter',
              backdrop: 0.5,
              html: '<div class="snotifyToast__title">NI Exists</div>' +
                  '<div class="snotifyToast__body">' +
                  '<strong>' + ni_num + ' already exists in the CRM.</strong><hr>' +
                  "Click 'OK' to go to the profile associated with this NI Number,<br><br>" +
                  "OR<br><br>" +
                  "Click 'CANCEL' to continue with this registration<br>(a different NI Number must be used)" +
                  '<div class="snotify-icon snotify-icon--confirm"><i class="fas fa-2x fa-exclamation-triangle"></i></div>' +
                  '</div>',
              buttons: [
                {
                  text: 'OK', action: (toast) => {
                    this.$store.commit('CURRENT_PROFILE_WORKER_ID', res.data.worker_id);
                    this.$snotify.remove(toast.id);
                    this.$nextTick(() => {
                      this.$tabs.close({
                        path: this.$router.currentRoute.fullPath,
                        to: '/workers/profile'
                      });
                      this.$tabs.refresh('/workers/profile', true);
                    })
                  }
                },
                {
                  text: 'CANCEL', action: (toast) => {
                    this.profile.ni_number = '';
                    this.$snotify.remove(toast.id)
                  }
                }
              ]
            });
          }
        })
      }
    },
    /**
     * PRIMARY / WEBSITE LOGIN EMAIL
     * ========================================================================================================================
     */
    /**
     * Email Check
     * -------------------------------
     * Set Personal Email address -> same as primary/website login email
     *
     * Check if email address exists as a 'Login/Primary' Email address in orca or not
     * If it does -> popup to redirect to profile of worker
     */
    check_if_email_exists(email) {
      if (email !== "") {
        // set personal email -> non-reactive
        this.emails.personal_email = Object.assign({}, this.emails.website_login)
        this.emails.personal_email.email_type_id = 64
        // check email
        axios.post(this.crmApiPath + 'worker/existing/primary_email', {email: email}).then(res => {
          if (res.data !== "") {
            this.$snotify.confirm('', '', {
              closeOnClick: false,
              position: 'centerCenter',
              backdrop: 0.5,
              html: '<div class="snotifyToast__title">Email Exists</div>' +
                  '<div class="snotifyToast__body">' +
                  '<strong>' + email + ' already exists in the CRM.</strong><hr>' +
                  "Click 'OK' to go to the profile associated with this email address,<br><br>" +
                  "OR<br><br>" +
                  "Click 'CANCEL' to continue with this registration<br>(a different email must be used)" +
                  '<div class="snotify-icon snotify-icon--confirm"><i class="fas fa-2x fa-exclamation-triangle"></i></div>' +
                  '</div>',
              buttons: [
                {
                  text: 'OK', action: (toast) => {
                    this.$store.commit('CURRENT_PROFILE_WORKER_ID', res.data.worker_id);
                    this.$snotify.remove(toast.id);
                    this.$nextTick(() => {
                      this.$tabs.close({
                        path: this.$router.currentRoute.fullPath,
                        to: '/workers/profile'
                      });
                      this.$tabs.refresh('/workers/profile', true);
                    })
                  }
                },
                {
                  text: 'CANCEL', action: (toast) => {
                    this.emails.website_login.email_address = '';
                    this.$snotify.remove(toast.id)
                  }
                }
              ]
            });
          }
        })
      }
    },
    /**
     * No Email Button
     * -------------------------------
     * If the worker being registered does not have an email address - or does not know it
     * Set the login and personal email addresses to a default value
     */
    no_email_address() {
      axios.get(this.crmApiPath + 'worker/existing/last_no_email').then(res => {
        this.emails.website_login.email_address = res.data + '@orca-no-email.com';
        this.emails.personal_email = Object.assign({}, this.emails.website_login)
        this.emails.personal_email.email_type_id = 64
      })
    },
    /**
     * CURRENT EMPLOYMENT STATUS
     * ========================================================================================================================
     */
    job_title_label({job_title, sector_name}) {
      return `${job_title} - ${sector_name}`;
    },
    employmentTypeStr() {
      if(this.company_info.employment_type && this.company_info.employment_type.value === 'Limited Company') {
        return this.company_info.employment_type.value;
      }

      return 'Personal';
    },
    showAdditionalBank() {
      if(this.company_info.employment_type) {

        if(this.company_info.employment_type.id === 32) {
          return false;
        }

        if(this.company_info.employment_type.id === 30) {
          return false;
        }

        if(this.company_info.employment_type.id === 29) {
          return false;
        }

      }

      return true;
    },
    /**
     * PERSONAL CONTACT INFORMATION
     * ========================================================================================================================
     */
    set_personal_address(place) {
      let address = this.set_google_address(place);
      // set all address variables
      this.addresses.home.building = address.building;
      this.addresses.home.street = address.street;
      this.addresses.home.locality = address.locality;
      this.addresses.home.city_town = address.city_town;
      this.addresses.home.county = address.county;
      this.addresses.home.postcode = address.postcode;
    },
    /**
     * COMPANY / SOLE TRADER INFORMATION
     * ========================================================================================================================
     */
    /**
     * Trading Name - Companies house search
     * -------------------------------
     * Search Companies house for company name as typing
     * If selected -> populate ltd reg number
     */
    search_companies_house() {
      if (this.company_info.company_trading_name !== '') {
        axios.post(this.crmApiPath + 'hmrc/company/search', {company_name: this.company_info.company_trading_name})
            .then(companies => {
              this.company_search_results = companies.data
              this.searching_ch = false;
            })
            .catch(error => console.log(error));
      }
    },
    trading_name_input() {
      if (this.company_info.company_trading_name !== '') {
        this.searching_ch = true;
        if (!this.awaitingTradingNameSearch) {
          setTimeout(() => {
            this.search_companies_house();
            this.awaitingTradingNameSearch = false;
          }, 1000);
        }
        this.awaitingTradingNameSearch = true;
      }
    },
    clear_company_search_result() {
      if (this.notEmptyObject(this.company_search_results)) {
        this.company_search_results = {};
      }
    },
    disabledHMRC(status) {
      if (status === 'dissolved') {
        return true;
      }
    },
    select_company(company_number, status) {
      if (status !== 'dissolved') {
        axios.post(this.crmApiPath + 'hmrc/company/profile', {company_number: company_number}).then(company => {
          this.company_info.company_trading_name = company.data.company_name;
          if (this.company_info.employment_type !== null && this.company_info.employment_type.id === 32) {
            this.company_info.company_ltd_reg_number = company.data.company_number;
          }
        }).catch(error => console.log(error));
        this.company_search_results = {};
      }
    },
    /**
     * Company Address
     */
    set_company_address(place) {
      let address = this.set_google_address(place);
      // set all address variables
      this.addresses.company.building = address.building;
      this.addresses.company.street = address.street;
      this.addresses.company.locality = address.locality;
      this.addresses.company.city_town = address.city_town;
      this.addresses.company.county = address.county;
      this.addresses.company.postcode = address.postcode;
    },
    /**
     * Check VAT Number
     * -------------------------------
     * Check HMRC for VAT number entered - retrieve results
     */
    check_vat_number() {
      this.vat_check = {};
      axios.get(this.crmApiPath + 'hmrc/vat/check', {
        params: {
          vat_number: this.company_info.vat_number
        }
      }).then(res => this.vat_check = res.data)
    },
    /**
     * RECRUITMENT / RESOURCING
     * ========================================================================================================================
     */
    /**
     * Filter job titles based on industries selected
     * -------------------------------
     */
    select_industry() {
      this.$nextTick(() => {
        let permittedValues = this.recruitment.industries.map(value => value.sector);
        this.ms_opts.rec_job_titles = this.ms_opts.rec_temp_job_titles.filter(function (jt) {
          return permittedValues.includes(jt.sector.sector)
        })
      })
    },
    /**
     * Format Emp 1 start date picked
     * -------------------------------
     */
    select_emp_1_start_date(date) {
      if (date !== null) {
        this.recruitment.prev_emp_1_start_date = this.$moment(date).format('YYYY-MM-DD');
      }
    },
    /**
     * Format Emp 1 end date picked
     * -------------------------------
     */
    select_emp_1_end_date(date) {
      if (date !== null) {
        this.recruitment.prev_emp_1_end_date = this.$moment(date).format('YYYY-MM-DD');
      }
    },
    /**
     * Format Emp 2 start date picked
     * -------------------------------
     */
    select_emp_2_start_date(date) {
      if (date !== null) {
        this.recruitment.prev_emp_2_start_date = this.$moment(date).format('YYYY-MM-DD');
      }
    },
    /**
     * Format Emp 2 end date picked
     * -------------------------------
     */
    select_emp_2_end_date(selected_dob) {
      if (selected_dob !== null) {
        this.recruitment.prev_emp_2_end_date = this.$moment(selected_dob).format('YYYY-MM-DD');
      }
    },
  },
  watch: {
    'person.nationality'(nationality) {
      let uk_ireland = ['United Kingdom (UK)', 'Wales', 'Scotland', 'Northern Ireland', 'England', 'Republic of Ireland'];
      if (nationality !== null) {
        this.person.is_uk_citizen = uk_ireland.includes(nationality.country_name);
      } else {
        this.person.is_uk_citizen = false;
      }
    },

    /**
     * CURRENT EMPLOYMENT STATUS
     * ========================================================================================================================
     */
    'company_info.employment_type'(val) {
      // Self Employed (CIS) - Sole Trader || Self Employed - Sole Trader
      if (val !== null && (val.id === 29 || val.id === 30)) {
        if (this.company_info.company_trading_name === "") {
          this.company_info.company_trading_name = this.person.first_name + ' ' + this.person.surname
        }
        this.company_info.company_utr_number = "";
        this.company_info.company_ltd_reg_number = "";
        this.company_info.company_address_same_as_personal = true;
        this.company_info.company_bank_same_as_personal = true;
        // services
        this.services.personal_accident_insurance = false;
      }
      // Limited
      else if (val !== null && val.id === 32) {
        // services
        this.services.personal_accident_insurance = false;
        this.company_info.company_bank_same_as_personal = true;
      }
      // PAYE (33)
      else {
        this.profile.personal_utr_number = "";
        this.company_info.company_trading_name = "";
        this.company_info.company_utr_number = "";
        this.company_info.company_ltd_reg_number = "";
        this.company_info.vat_registered = false;
        this.company_info.company_address_same_as_personal = true;
        this.company_info.company_bank_same_as_personal = true;
        // services
        this.services.personal_accident_insurance = false;
        this.services.genius_tax = false;
      }
    },
    /**
     * PERSONAL CONTACT INFORMATION
     * ========================================================================================================================
     */
    /**
     * Personal Address
     */
    'addresses.home.building'(building) {
      this.person_place_full = this.calc_nice_address(building, this.addresses.home.street, this.addresses.home.locality, this.addresses.home.city_town, this.addresses.home.county, this.addresses.home.postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.building = building;
      }
    },
    'addresses.home.street'(street) {
      this.person_place_full = this.calc_nice_address(this.addresses.home.building, street, this.addresses.home.locality, this.addresses.home.city_town, this.addresses.home.county, this.addresses.home.postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.street = street
      }
    },
    'addresses.home.locality'(locality) {
      this.person_place_full = this.calc_nice_address(this.addresses.home.building, this.addresses.home.street, locality, this.addresses.home.city_town, this.addresses.home.county, this.addresses.home.postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.locality = locality
      }
    },
    'addresses.home.city_town'(city_town) {
      this.person_place_full = this.calc_nice_address(this.addresses.home.building, this.addresses.home.street, this.addresses.home.locality, city_town, this.addresses.home.county, this.addresses.home.postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.city_town = city_town
      }
    },
    'addresses.home.county'(county) {
      this.person_place_full = this.calc_nice_address(this.addresses.home.building, this.addresses.home.street, this.addresses.home.locality, this.addresses.home.city_town, county, this.addresses.home.postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.county = county
      }
    },
    'addresses.home.postcode'(postcode) {
      this.person_place_full = this.calc_nice_address(this.addresses.home.building, this.addresses.home.street, this.addresses.home.locality, this.addresses.home.city_town, this.addresses.home.county, postcode);
      if (this.company_info.company_address_same_as_personal) {
        this.addresses.company.postcode = postcode
      }
    },
    /**
     * PERSONAL BANK INFORMATION
     * ========================================================================================================================
     */
    'banks.personal.bank_name'(bank_name) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.bank_name = bank_name;
      }
    },
    'banks.personal.accounts_holder'(accounts_holder) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.accounts_holder = accounts_holder;
      }
    },
    'banks.personal.account_number'(account_number) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.account_number = account_number;
      }
    },
    'banks.personal.sort_code'(sort_code) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.sort_code = sort_code;
      }
    },
    'banks.personal.building_society_ref'(bsoc_ref) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.building_society_ref = bsoc_ref;
      }
    },
    'banks.personal.branch_location'(branch_location) {
      if (this.company_info.company_bank_same_as_personal) {
        this.banks.company.branch_location = branch_location;
      }
    },
    /**
     * COMPANY INFORMATION
     * ========================================================================================================================
     */
    'company_info.vat_registered'(val) {
      if (!val) {
        this.company_info.vat_number = "";
        this.vat_check = {}
      }
    },
    'company_info.vat_number'(val) {
        this.vat_check = {}
    },
    'company_info.company_address_same_as_personal'(val) {
      if (val) {
        this.addresses.company.building = this.addresses.home.building;
        this.addresses.company.street = this.addresses.home.street;
        this.addresses.company.locality = this.addresses.home.locality;
        this.addresses.company.city_town = this.addresses.home.city_town;
        this.addresses.company.county = this.addresses.home.county;
        this.addresses.company.postcode = this.addresses.home.postcode;
      }
      else if (!val) {
        this.addresses.company.building = '';
        this.addresses.company.street = '';
        this.addresses.company.locality = '';
        this.addresses.company.city_town = '';
        this.addresses.company.county = '';
        this.addresses.company.postcode = ''
      }
    },
    'company_info.company_bank_same_as_personal'(val) {
      if (val) {
        this.banks.company.bank_name = this.banks.personal.bank_name;
        this.banks.company.branch_location = this.banks.personal.branch_location;
        this.banks.company.accounts_holder = this.banks.personal.accounts_holder;
        this.banks.company.account_number = this.banks.personal.account_number;
        this.banks.company.sort_code = this.banks.personal.sort_code;
        this.banks.company.building_society_ref = this.banks.personal.building_society_ref;
      }
      else if (!val) {
        this.banks.company.bank_name = '';
        this.banks.company.branch_location = '';
        this.banks.company.accounts_holder = '';
        this.banks.company.account_number = '';
        this.banks.company.sort_code = '';
        this.banks.company.building_society_ref = '';
      }
    },
    /**
     * Company Address
     */
    'addresses.company.building'(building) {
      this.company_place_full = this.calc_nice_address(building, this.addresses.company.street, this.addresses.company.locality, this.addresses.company.city_town, this.addresses.company.county, this.addresses.company.postcode);
    },
    'addresses.company.street'(street) {
      this.company_place_full = this.calc_nice_address(this.addresses.company.building, street, this.addresses.company.locality, this.addresses.company.city_town, this.addresses.company.county, this.addresses.company.postcode);
    },
    'addresses.company.locality'(locality) {
      this.company_place_full = this.calc_nice_address(this.addresses.company.building, this.addresses.company.street, locality, this.addresses.company.city_town, this.addresses.company.county, this.addresses.company.postcode);
    },
    'addresses.company.city_town'(city_town) {
      this.company_place_full = this.calc_nice_address(this.addresses.company.building, this.addresses.company.street, this.addresses.company.locality, city_town, this.addresses.company.county, this.addresses.company.postcode);
    },
    'addresses.company.county'(county) {
      this.company_place_full = this.calc_nice_address(this.addresses.company.building, this.addresses.company.street, this.addresses.company.locality, this.addresses.company.city_town, county, this.addresses.company.postcode);
    },
    'addresses.company.postcode'(postcode) {
      this.company_place_full = this.calc_nice_address(this.addresses.company.building, this.addresses.company.street, this.addresses.company.locality, this.addresses.company.city_town, this.addresses.company.county, postcode);
    },
    /**
     * SERVICES
     * ========================================================================================================================
     */
    'services.recruitment'(val) {
      if (!val) {
        this.recruitment = {
          worker_id: null,
          job_types: null,
          industries: [],
          job_titles: null,
          any_health_disability: false,
          health_disability_details: '',
          health_disability_special_requirements: '',
          have_own_transport: false,
          distance_willing_to_travel: '',
          any_criminal_convictions: false,
          criminal_convictions_details: '',
          education: '',
          qualifications: null,
          prev_emp_1_company: '',
          prev_emp_1_address: '',
          prev_emp_1_start_date: null,
          prev_emp_1_start_date_disabled: {
            to: '',
            from: ''
          },
          prev_emp_1_end_date: null,
          prev_emp_1_end_date_disabled: {
            to: '',
            from: '',
          },
          prev_emp_1_position: '',
          prev_emp_1_reason_for_leaving: '',
          prev_emp_1_salary: '',
          prev_emp_2_company: '',
          prev_emp_2_address: '',
          prev_emp_2_start_date: null,
          prev_emp_2_start_date_disabled: {
            to: '',
            from: ''
          },
          prev_emp_2_end_date: null,
          prev_emp_2_end_date_disabled: {
            to: '',
            from: '',
          },
          prev_emp_2_position: '',
          prev_emp_2_reason_for_leaving: '',
          prev_emp_2_salary: '',
          reference_1_name: '',
          reference_1_company_relationship: '',
          reference_1_telephone: '',
          reference_1_email: '',
          reference_2_name: '',
          reference_2_company_relationship: '',
          reference_2_telephone: '',
          reference_2_email: '',
          immigration_permission: false,
          agree_48_hour_opt_out: false,
          agree_equal_op: false,
          agree_dpa: false,
          agree_contract_services: false,
          log_by_type: 'App\\Models\\Users\\User',
          log_by_id: this.$store.state.auth.me.id
        }
      }
    }
  }
}
</script>

<style lang="scss">
.vat_check_display {
  border:        dashed 2px $green;
  border-radius: $border_radius_2;
  margin-bottom: 20px;
  padding:       10px;

  i {
    color:         $white;
    background:    $green;
    padding:       13px;
    border-radius: $border_radius_2;
  }

  strong, .vat_val {
    display:        inline-block;
    vertical-align: top;
    color:          $green;
  }

  strong {
    width: 100px;
  }

  &.different {
    border-color: $red;

    i {
      background: $red;
    }

    strong, .vat_val {
      color: $red;
    }
  }
}

.no_email_warning {
  border:        dashed 3px $red;
  border-radius: $border_radius_2;
  margin-bottom: 20px;
  padding:       10px;
  text-align:    center;

  h4, p {
    color: $red;
  }
}
</style>
